import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import {
  IFileUploadRequest,
  IFileUploadResponse,
} from '../../state/models/fileupload.model';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private readonly baseUrl = `${environment.assetsEndpoint}/api/assets`;

  constructor(
    private readonly apiService: ApiService,
    private readonly http: HttpClient
  ) {}

  /**
   * Generate presigned URL for file upload
   * @param request The file upload request containing clientId, fileName, and folderName
   * @returns Promise with presigned URL response
   */
  async generatePresignedUrl(
    request: IFileUploadRequest
  ): Promise<IFileUploadResponse> {
    try {
      const response = await this.apiService.post<{data:IFileUploadResponse}>(
        this.baseUrl+'/image',
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error generating presigned URL:', error);
      throw error;
    }
  }

  /**
   * Upload file to S3 using presigned URL
   * @param presignedUrl The presigned URL from generatePresignedUrl
   * @param file The file to upload
   * @returns Observable that tracks upload progress
   */
  uploadFileToS3(presignedUrl: string, file: File): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
    });

    return this.http.put(presignedUrl, file, {
      headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  /**
   * Complete file upload process (generate URL + upload file)
   * @param request The file upload request
   * @param file The file to upload
   * @returns Observable for the complete upload process
   */
  uploadFile(request: IFileUploadRequest, file: File): Observable<any> {
    return from(
      this.generatePresignedUrl(request).then((response) => ({
        presignedResponse: response,
        uploadObservable: this.uploadFileToS3(response.url, file),
      }))
    );
  }
}
